import { useState } from 'react';
import { Button, Card } from '@lms/ui';
import { X, BookOpen } from 'lucide-react';
import { useCreateSubject, useUpdateSubject } from '../hooks/useProgramSubjects';
import type { ProgramSubject } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface SubjectModalProps {
  programId: string;
  stageId: string;
  stageName?: string;
  order: number;
  subject?: ProgramSubject;
  availablePrerequisites?: ProgramSubject[];
  onClose: () => void;
}

export function SubjectModal({
  programId,
  stageId,
  stageName,
  order,
  subject,
  availablePrerequisites = [],
  onClose,
}: SubjectModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState(subject?.name || '');
  const [code, setCode] = useState(subject?.code || '');
  const [kitabRujukan, setKitabRujukan] = useState(subject?.kitabRujukan || '');
  const [teacherName, setTeacherName] = useState(subject?.teacherName || '');
  const [isSequential, setIsSequential] = useState(subject?.isSequential ?? true);
  const [prerequisiteSubjectId, setPrerequisiteSubjectId] = useState(
    subject?.prerequisiteSubjectId || ''
  );
  const [description, setDescription] = useState(subject?.description || '');

  const createSubjectMutation = useCreateSubject();
  const updateSubjectMutation = useUpdateSubject();

  const isEditing = !!subject;
  const isPending = createSubjectMutation.isPending || updateSubjectMutation.isPending;

  // Filter out current subject from available prerequisites
  const filteredPrereqs = availablePrerequisites.filter(p => !isEditing || p.id !== subject.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (isEditing) {
      updateSubjectMutation.mutate(
        {
          id: subject.id,
          programId,
          data: {
            name: name.trim(),
            code: code.trim() || undefined,
            kitabRujukan: kitabRujukan.trim() || undefined,
            teacherName: teacherName.trim() || undefined,
            isSequential,
            prerequisiteSubjectId: prerequisiteSubjectId || undefined,
            description: description.trim() || undefined,
            order,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      createSubjectMutation.mutate(
        {
          programId,
          data: {
            stageId,
            name: name.trim(),
            code: code.trim() || undefined,
            kitabRujukan: kitabRujukan.trim() || undefined,
            teacherName: teacherName.trim() || undefined,
            isSequential,
            prerequisiteSubjectId: prerequisiteSubjectId || undefined,
            description: description.trim() || undefined,
            order,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <Card className="w-full max-w-lg !p-0 overflow-hidden shadow-level5 border border-on-surface/5 animate-scaleIn my-auto">
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <BookOpen size={20} />
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                <Bilingual
                  {...(isEditing ? copy.subjectModal.titleEdit : copy.subjectModal.titleCreate)}
                />
              </h3>
              <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                {isEditing
                  ? subject.name
                  : stageName
                    ? `${stageName} • Mata Pelajaran #${String(order).padStart(2, '0')}`
                    : `Mata Pelajaran #${String(order).padStart(2, '0')}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-container-high rounded-full transition-colors text-on-surface-variant"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <label
                  htmlFor="subject-name"
                  className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
                >
                  <Bilingual {...copy.subjectModal.labelName} />{' '}
                  <span className="text-error">*</span>
                </label>
                <input
                  id="subject-name"
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderName)}
                  className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject-code"
                  className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
                >
                  <Bilingual {...copy.subjectModal.labelCode} />
                </label>
                <input
                  id="subject-code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderCode)}
                  className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="subject-kitab"
                  className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
                >
                  <Bilingual {...copy.subjectModal.labelKitab} />
                </label>
                <input
                  id="subject-kitab"
                  type="text"
                  value={kitabRujukan}
                  onChange={e => setKitabRujukan(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderKitab)}
                  className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject-teacher"
                  className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
                >
                  <Bilingual {...copy.subjectModal.labelTeacher} />
                </label>
                <input
                  id="subject-teacher"
                  type="text"
                  value={teacherName}
                  onChange={e => setTeacherName(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderTeacher)}
                  className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="subject-prereq"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.subjectModal.labelPrerequisite} />
              </label>
              <select
                id="subject-prereq"
                value={prerequisiteSubjectId}
                onChange={e => setPrerequisiteSubjectId(e.target.value)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              >
                <option value="">{t(copy.subjectModal.nonePrerequisite)}</option>
                {filteredPrereqs.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.code ? `(${p.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 p-3 bg-surface-container-low rounded-xl">
              <input
                type="checkbox"
                id="subject-sequential"
                checked={isSequential}
                onChange={e => setIsSequential(e.target.checked)}
                className="rounded border-outline-variant text-primary focus:ring-primary h-4 w-4"
              />
              <label
                htmlFor="subject-sequential"
                className="text-xs font-semibold text-on-surface cursor-pointer select-none"
              >
                <span className="block font-bold">
                  <Bilingual {...copy.subjectModal.labelSequential} />
                </span>
                <span className="text-[10px] text-on-surface-variant font-normal">
                  <Bilingual {...copy.subjectModal.descSequential} />
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="subject-desc"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.subjectModal.labelDescription} />
              </label>
              <textarea
                id="subject-desc"
                rows={2}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t(copy.subjectModal.placeholderDescription)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/10">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
              <Bilingual {...copy.levelModal.abort} />
            </Button>
            <Button variant="primary" type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <Bilingual {...copy.subjectModal.btnSaving} />
              ) : isEditing ? (
                <Bilingual {...copy.subjectModal.btnSubmitEdit} />
              ) : (
                <Bilingual {...copy.subjectModal.btnSubmitCreate} />
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
